import { useState, useEffect } from 'react'
import { useLocale } from '../../App'

interface HistoryViewProps {
  authToken: string | null
  onBack: () => void
}

interface HistoryEntry {
  id: string
  winner: string
  winningHandType: string
  chipChange: number
  startedAt: string
}

export function HistoryView({ authToken, onBack }: HistoryViewProps) {
  const { t } = useLocale()
  const [entries, setEntries] = useState<HistoryEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [date, setDate] = useState('')
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState('')

  const fetchHistory = async (p: number, d: string) => {
    if (!authToken) return
    setLoading(true)
    setFetchError('')
    try {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), 15000)
      const params = new URLSearchParams({ page: String(p) })
      if (d) params.set('date', d)
      const res = await fetch(`/api/game/history?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
        signal: controller.signal,
      })
      clearTimeout(timer)
      const data = await res.json()
      setEntries(data.entries || [])
      setTotal(data.total || 0)
      setPage(data.page || 1)
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setFetchError(t('app.history_timeout'))
      } else {
        setFetchError(t('app.history_error'))
      }
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchHistory(1, date)
  }, [date, authToken])

  const totalPages = Math.ceil(total / 20)

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'linear-gradient(180deg, #0a0e1a 0%, #141b2d 100%)' }}>
      {/* Header */}
      <div className="shrink-0 px-4 pt-3 pb-2 flex items-center gap-3 border-b border-amber-500/20" style={{ background: 'linear-gradient(135deg, rgba(180,120,50,0.08), rgba(180,120,50,0.02))' }}>
        <button onClick={onBack} className="text-amber-400/80 hover:text-amber-300 text-xl">{t('app.back')}</button>
        <div>
          <h2 className="text-lg font-bold text-amber-100/90 tracking-wide">{t('app.history_title')}</h2>
          <p className="text-[11px] text-amber-400/50">GAME HISTORY</p>
        </div>
      </div>

      {/* Date filter */}
      <div className="shrink-0 px-4 py-2">
        <input
          type="date"
          value={date}
          onChange={(e) => { setDate(e.target.value); setPage(1) }}
          className="w-full rounded-lg bg-slate-800/60 border border-amber-500/20 px-3 py-2 text-sm text-amber-100/80 outline-none focus:border-amber-500/40"
          style={{ colorScheme: 'dark' }}
        />
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 space-y-2 pb-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-6 h-6 border-2 border-amber-500/30 border-t-amber-400 rounded-full animate-spin" />
          </div>
        ) : fetchError ? (
          <div className="flex flex-col items-center gap-3 py-16">
            <div className="text-red-400/70 text-sm">{fetchError}</div>
            <button onClick={() => fetchHistory(1, date)} className="px-4 py-2 rounded-lg text-xs font-bold text-amber-300 border border-amber-500/30 bg-amber-500/10">
              {t('app.history_reload')}
            </button>
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-amber-400/30">
            <span className="text-4xl">📋</span>
            <span className="text-sm">{t('app.history_empty')}</span>
          </div>
        ) : entries.map((e) => {
          const isWin = e.winner === 'user'
          const isTie = e.winner === 'tie'
          const sign = isWin ? '+' : isTie ? '' : '-'
          const color = isWin ? 'text-emerald-400' : isTie ? 'text-amber-400' : 'text-red-400'
          const label = isWin ? t('app.history_win') : isTie ? t('app.history_tie') : t('app.history_lose')
          return (
            <div
              key={e.id}
              className="rounded-xl px-4 py-3 border backdrop-blur-sm"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))',
                borderColor: isWin ? 'rgba(52,211,153,0.2)' : isTie ? 'rgba(251,191,36,0.15)' : 'rgba(239,68,68,0.15)',
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    isWin ? 'bg-emerald-500/20 text-emerald-400' : isTie ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {isWin ? '🤖' : isTie ? '🤝' : '🤖'}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-amber-100/90">{t('app.history_ai')}</div>
                    <div className="text-[11px] text-amber-400/40">
                      {new Date(e.startedAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-bold ${color}`}>{label}</div>
                  <div className={`text-sm font-bold tabular-nums ${color}`}>
                    {sign}{Math.abs(e.chipChange).toLocaleString()}
                  </div>
                </div>
              </div>
              {e.winningHandType && (
                <div className="mt-1.5 text-[11px] text-amber-400/40">
                  {t('app.history_hand')}{e.winningHandType}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="shrink-0 flex items-center justify-center gap-3 px-4 py-3 border-t border-amber-500/10">
          <button
            onClick={() => fetchHistory(page - 1, date)}
            disabled={page <= 1}
            className="px-3 py-1 rounded text-xs font-semibold text-amber-400/60 disabled:text-amber-400/20 border border-amber-500/20 disabled:border-amber-500/10"
          >{t('app.history_prev')}</button>
          <span className="text-xs text-amber-400/40">{page} / {totalPages}</span>
          <button
            onClick={() => fetchHistory(page + 1, date)}
            disabled={page >= totalPages}
            className="px-3 py-1 rounded text-xs font-semibold text-amber-400/60 disabled:text-amber-400/20 border border-amber-500/20 disabled:border-amber-500/10"
          >{t('app.history_next')}</button>
        </div>
      )}
    </div>
  )
}
